/**
 * Корневой экран: карта улья на данных сервера, кошелёк, карточка соты.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { LocaleCode } from "@hive/i18n";
import { isRitualTraceQuality, type SupportCategory, type TraceQuality } from "@hive/shared";
import type { MapHexCard } from "@hive/shared/api/map";
import { gridDisk } from "h3-js";
import { useTranslation } from "react-i18next";

import { refresh as refreshTokens, resendVerification, verifyEmail } from "./api/auth.js";
import { ApiError, errorI18nKey } from "./api/client.js";
import { collectVault } from "./api/collect.js";
import { convertCash } from "./api/convert.js";
import { fetchHearth, moveHearth, placeHearth, type HearthView } from "./api/hearth.js";
import { fetchHexCard, fetchHexes, type MapBounds } from "./api/map.js";
import { fetchProfile, type PlayerProfile } from "./api/profile.js";
import { fetchFeed, markFeedRead, type FeedEventView } from "./api/feed.js";
import {
  claimDailyQuest,
  fetchDailyQuests,
  type DailyQuestView,
} from "./api/quests.js";
import { performRitual } from "./api/ritual.js";
import {
  submitSupportReport,
  type SupportReportContext,
} from "./api/support.js";
import { fetchTraces, submitTraceBatch, type TraceView } from "./api/traces.js";
import {
  fetchDebts,
  fetchEconomySummary,
  fetchLedger,
  fetchWallet,
  type DebtItemView,
  type EconomySummaryView,
  type LedgerEntryView,
  type ServerWalletView,
} from "./api/wallet.js";
import { walletViewFromServer } from "./game/economy.js";
import { nextOnboardingStep, type OnboardingStep } from "./game/onboarding.js";
import { parseHeroAppearance, type HeroAppearanceId } from "./game/appearance.js";
import { HERO_LEVEL_CAP } from "./game/hero.js";
import { loadUiPrefs, saveUiPrefs } from "./game/player.js";
import { clearClientErrors, recordClientError, snapshotClientErrors } from "./game/client-errors.js";
import { getTraceTelemetry } from "./game/trace-telemetry.js";
import { buildDebugTracePoints } from "./game/traces-debug.js";
import { startTraceTracker } from "./geo/tracker.js";
import {
  clearSession,
  loadSession,
  refreshSessionOnce,
  saveSession,
  type Session,
} from "./game/session.js";
import { HiveMap } from "./map/HiveMap.js";
import { MIN_HEX_ZOOM, distanceMeters, hexCenter } from "./map/hexes.js";
import { AppNav, type AppTab } from "./ui/AppNav.jsx";
import { AuthPanel } from "./ui/AuthPanel.jsx";
import { VerifyBanner } from "./ui/auth/VerifyBanner.jsx";
import { DebtScreen } from "./ui/DebtScreen.jsx";
import { HexCard } from "./ui/HexCard.jsx";
import { HintBar } from "./ui/HintBar.jsx";
import { Legend } from "./ui/Legend.jsx";
import { OnboardingSheet } from "./ui/OnboardingSheet.jsx";
import { ProfileBar } from "./ui/profile/ProfileBar.jsx";
import { WalletBar } from "./ui/WalletBar.jsx";
import { FeedScreen } from "./ui/FeedScreen.jsx";
import { QuestScreen } from "./ui/QuestScreen.jsx";
import { HearthScreen } from "./ui/HearthScreen.jsx";
import { HeroScreen } from "./ui/HeroScreen.jsx";
import { TracesScreen } from "./ui/TracesScreen.jsx";
import { ReportProblemSheet } from "./ui/ReportProblemSheet.jsx";
import { SettingsScreen } from "./ui/SettingsScreen.jsx";
import { WalletScreen } from "./ui/WalletScreen.jsx";
import "./ui/auth/auth.css";

const DEFAULT_CENTER = { lat: 55.16, lon: 61.4 } as const;

export interface AppProps {
  readonly locale: LocaleCode;
}

export function App({ locale }: AppProps): React.JSX.Element {
  const { t } = useTranslation(["common", "errors", "feed", "quests", "traces", "hearth"]);
  const [session, setSession] = useState<Session | null>(() => loadSession());
  const [profile, setProfile] = useState<PlayerProfile | null>(null);
  const [walletServer, setWalletServer] = useState<ServerWalletView | null>(null);
  const [selectedH3, setSelectedH3] = useState<string | null>(null);
  const [hexCard, setHexCard] = useState<MapHexCard | null>(null);
  const [zoom, setZoom] = useState(16);
  const [loading, setLoading] = useState(false);
  const [hexLoading, setHexLoading] = useState(false);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [actionBusy, setActionBusy] = useState(false);
  const [tab, setTab] = useState<AppTab>("map");
  const [summary, setSummary] = useState<EconomySummaryView | null>(null);
  const [ledger, setLedger] = useState<LedgerEntryView[]>([]);
  const [ledgerLoading, setLedgerLoading] = useState(false);
  const [ledgerHasMore, setLedgerHasMore] = useState(false);
  const [debts, setDebts] = useState<DebtItemView[]>([]);
  const [debtsLoading, setDebtsLoading] = useState(false);
  const [verifyNotice, setVerifyNotice] = useState<string | null>(null);
  const [traces, setTraces] = useState<TraceView[]>([]);
  const [mapRefreshKey, setMapRefreshKey] = useState(0);
  const [captureHexId, setCaptureHexId] = useState<string | null>(null);
  const [feedEvents, setFeedEvents] = useState<FeedEventView[]>([]);
  const [feedUnread, setFeedUnread] = useState(0);
  const [feedOpen, setFeedOpen] = useState(false);
  const [feedLoading, setFeedLoading] = useState(false);
  const [dailyQuests, setDailyQuests] = useState<DailyQuestView[]>([]);
  const [questEcoDayEndsAt, setQuestEcoDayEndsAt] = useState<string>("");
  const [questOpen, setQuestOpen] = useState(false);
  const [questLoading, setQuestLoading] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [reportContext, setReportContext] = useState<SupportReportContext | undefined>(
    undefined,
  );
  const [reportNoticeKey, setReportNoticeKey] = useState<string | null>(null);
  const [reportNoticeParams, setReportNoticeParams] = useState<Record<string, string>>({});
  const [hearthView, setHearthView] = useState<HearthView | null>(null);
  const [hearthLoading, setHearthLoading] = useState(false);
  const [hearthMoveMode, setHearthMoveMode] = useState(false);
  const [tracesOpen, setTracesOpen] = useState(false);
  const [verifyHidden, setVerifyHidden] = useState(
    () => loadUiPrefs().verifyHiddenFor !== null,
  );
  const [gpsHint, setGpsHint] = useState<string | null>(null);
  const [introOpen, setIntroOpen] = useState(() => !loadUiPrefs().onboardingDone);
  const [hintDismissed, setHintDismissed] = useState<OnboardingStep>(null);
  /**
   * Облик героя держится здесь, а не на экране героя: тот же аватар показывает
   * шапка профиля, и при локальном состоянии экрана она отставала до
   * перезагрузки страницы.
   */
  const [appearance, setAppearance] = useState<HeroAppearanceId>(() =>
    parseHeroAppearance(loadUiPrefs().heroAppearance),
  );

  const uiPrefs = useMemo(() => loadUiPrefs(), []);
  const timeZone = profile?.hearthTz ?? "UTC";

  const setAppError = useCallback((code: string | null) => {
    if (code) recordClientError(code);
    setErrorCode(code);
  }, []);

  const authorizedFetch = useCallback(
    async <T,>(fn: (token: string, signal: AbortSignal) => Promise<T>, signal?: AbortSignal): Promise<T> => {
      if (!session) throw new ApiError("auth.required", 401);
      const used = session.accessToken;
      try {
        return await fn(used, signal ?? new AbortController().signal);
      } catch (err) {
        if (!(err instanceof ApiError) || err.code !== "auth.required") throw err;

        // Сосед по пачке запросов мог обновить токен, пока этот летел: тогда
        // обмен не нужен, достаточно повторить с уже свежим токеном.
        const stored = loadSession();
        if (stored && stored.accessToken !== used) {
          setSession(stored);
          return fn(stored.accessToken, signal ?? new AbortController().signal);
        }

        const next = await refreshSessionOnce(refreshTokens);
        if (!next) throw err;
        setSession(next);
        return fn(next.accessToken, signal ?? new AbortController().signal);
      }
    },
    [session],
  );

  const loadTraces = useCallback(async () => {
    if (!session) return;
    try {
      const data = await authorizedFetch((token, signal) => fetchTraces(token, signal));
      setTraces([...data.traces]);
    } catch (err) {
      setAppError(err instanceof ApiError ? err.code : "network.offline");
    }
  }, [authorizedFetch, session, setAppError]);

  const loadHearth = useCallback(async () => {
    if (!session) return;
    setHearthLoading(true);
    try {
      const hearth = await authorizedFetch((token, signal) => fetchHearth(token, signal));
      setHearthView(hearth);
    } catch (err) {
      if (err instanceof ApiError && err.code === "hearth.not_placed") {
        setHearthView(null);
      } else {
        setAppError(err instanceof ApiError ? err.code : "network.offline");
      }
    } finally {
      setHearthLoading(false);
    }
  }, [authorizedFetch, session, setAppError]);

  const loadQuests = useCallback(
    async (autoOpen = false) => {
      if (!session) return;
      setQuestLoading(true);
      try {
        const data = await authorizedFetch((token, signal) => fetchDailyQuests(token, signal));
        setDailyQuests([...data.quests]);
        setQuestEcoDayEndsAt(data.ecoDayEndsAt);
        if (autoOpen) {
          setQuestOpen(true);
        }
      } catch (err) {
        setAppError(err instanceof ApiError ? err.code : "network.offline");
      } finally {
        setQuestLoading(false);
      }
    },
    [authorizedFetch, session],
  );

  const loadFeed = useCallback(
    async (autoOpen = false) => {
      if (!session) return;
      setFeedLoading(true);
      try {
        const data = await authorizedFetch((token, signal) => fetchFeed(token, signal));
        setFeedEvents([...data.events]);
        setFeedUnread(data.unreadCount);
        if (autoOpen && data.unreadCount > 0) {
          setFeedOpen(true);
        }
      } catch (err) {
        setAppError(err instanceof ApiError ? err.code : "network.offline");
      } finally {
        setFeedLoading(false);
      }
    },
    [authorizedFetch, session],
  );

  const loadBootstrap = useCallback(async () => {
    if (!session) return;
    setLoading(true);
    setErrorCode(null);
    try {
      const [me, wallet] = await Promise.all([
        authorizedFetch((token, signal) => fetchProfile(token, signal)),
        authorizedFetch((token, signal) => fetchWallet(token, signal)),
      ]);
      setProfile(me);
      setWalletServer(wallet);
      saveUiPrefs({ lastEmail: session.email });
      await Promise.all([loadTraces(), loadHearth()]);
      const inDebt = wallet.debt > 0;
      await loadQuests(inDebt);
      await loadFeed(!inDebt);
    } catch (err) {
      const code = err instanceof ApiError ? err.code : "network.offline";
      setAppError(code);
      if (code === "auth.refresh_invalid" || code === "auth.required") {
        clearSession();
        setSession(null);
      }
    } finally {
      setLoading(false);
    }
  }, [authorizedFetch, loadFeed, loadHearth, loadQuests, loadTraces, session]);

  useEffect(() => {
    if (session) {
      saveSession(session);
      void loadBootstrap();
    }
  }, [session, loadBootstrap]);

  // Связь вернулась — сами повторяем загрузку, не заставляя игрока жать «ещё раз».
  useEffect(() => {
    if (!session) return;
    const retry = (): void => void loadBootstrap();
    window.addEventListener("online", retry);
    return () => window.removeEventListener("online", retry);
  }, [loadBootstrap, session]);

  // Трекер читает вкладку через ссылку: иначе каждое переключение вкладки
  // перезапускало GPS-подписку и телефон опрашивал спутники вдвое чаще.
  const tabRef = useRef(tab);
  useEffect(() => {
    tabRef.current = tab;
  }, [tab]);

  useEffect(() => {
    if (!session) return;
    return startTraceTracker({
      getAccessToken: () => session.accessToken,
      permissionRationale: t("common:location.permission_rationale"),
      mapVisible: () => tabRef.current === "map",
      onSynced: () => {
        setGpsHint(null);
        void loadTraces();
      },
      // Слабый сигнал — не отказ игры, а состояние города: красная плашка
      // ошибки на месте карты выглядела поломкой приложения.
      onError: (code) => (code === "gps.weak" ? setGpsHint(code) : setAppError(code)),
    });
  }, [loadTraces, session, setAppError, t]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get("verify");
    if (!token) return;
    params.delete("verify");
    const nextUrl = `${window.location.pathname}${params.toString() ? `?${params}` : ""}`;
    window.history.replaceState({}, "", nextUrl);
    void verifyEmail(token)
      .then(() => setVerifyNotice("common:auth.verify_success"))
      .catch((err: unknown) => {
        setVerifyNotice(
          err instanceof ApiError ? errorI18nKey(err.code) : "errors:network.offline",
        );
      });
  }, []);

  const handleCloseIntro = useCallback(() => {
    setIntroOpen(false);
    saveUiPrefs({ onboardingDone: true });
  }, []);

  const handleShowIntro = useCallback(() => {
    setIntroOpen(true);
    setHintDismissed(null);
    setSettingsOpen(false);
    setTab("map");
  }, []);

  const handleDismissVerify = useCallback(() => {
    setVerifyHidden(true);
    saveUiPrefs({ verifyHiddenFor: session?.email ?? null });
  }, [session?.email]);

  const handleShowVerify = useCallback(() => {
    setVerifyHidden(false);
    saveUiPrefs({ verifyHiddenFor: null });
    setSettingsOpen(false);
    setTab("map");
  }, []);

  const handleResendVerification = useCallback(async () => {
    if (!session) return;
    setErrorCode(null);
    try {
      await authorizedFetch((token) => resendVerification(token));
      setVerifyNotice("common:auth.verify_pending");
    } catch (err) {
      setAppError(err instanceof ApiError ? err.code : "network.offline");
    }
  }, [authorizedFetch, session]);

  useEffect(() => {
    if (!session || !selectedH3) {
      setHexCard(null);
      return;
    }
    const controller = new AbortController();
    setHexLoading(true);
    void authorizedFetch((token) => fetchHexCard(selectedH3, token, controller.signal))
      .then(setHexCard)
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        setAppError(err instanceof ApiError ? err.code : "network.offline");
        setHexCard(null);
      })
      .finally(() => {
        if (!controller.signal.aborted) setHexLoading(false);
      });
    return () => controller.abort();
  }, [authorizedFetch, selectedH3, session]);

  const hearthH3 = profile?.hearthHex ?? null;

  const hearthRing = useMemo(
    () => (hearthH3 ? new Set(gridDisk(hearthH3, 1)) : new Set<string>()),
    [hearthH3],
  );

  const wallet = useMemo(() => {
    if (!walletServer || !profile) return null;
    return walletViewFromServer(
      walletServer,
      profile.level,
      summary?.tributeCapLeft ?? 0,
    );
  }, [walletServer, profile, summary?.tributeCapLeft]);

  const loadWalletScreen = useCallback(async () => {
    if (!session) return;
    setLedgerLoading(true);
    setErrorCode(null);
    try {
      const [summaryData, ledgerData] = await Promise.all([
        authorizedFetch((token, signal) => fetchEconomySummary(token, signal)),
        authorizedFetch((token, signal) => fetchLedger(token, {}, signal)),
      ]);
      setSummary(summaryData);
      setLedger(ledgerData.entries);
      setLedgerHasMore(ledgerData.entries.length >= 100);
    } catch (err) {
      setAppError(err instanceof ApiError ? err.code : "network.offline");
    } finally {
      setLedgerLoading(false);
    }
  }, [authorizedFetch, session]);

  const loadDebts = useCallback(async () => {
    if (!session) return;
    setDebtsLoading(true);
    setErrorCode(null);
    try {
      const data = await authorizedFetch((token, signal) => fetchDebts(token, signal));
      setDebts(data.debts);
    } catch (err) {
      setAppError(err instanceof ApiError ? err.code : "network.offline");
    } finally {
      setDebtsLoading(false);
    }
  }, [authorizedFetch, session]);

  const handleConvertCash = useCallback(
    async (amount: number) => {
      if (!session) return;
      setActionBusy(true);
      setErrorCode(null);
      try {
        const result = await authorizedFetch((token) =>
          convertCash(amount, true, token, `convert-web-${Date.now()}`),
        );
        setWalletServer((prev) =>
          prev
            ? {
                ...prev,
                cash: result.cash,
                hive: result.hive,
              }
            : prev,
        );
        await loadWalletScreen();
      } catch (err) {
        setAppError(err instanceof ApiError ? err.code : "network.offline");
        throw err;
      } finally {
        setActionBusy(false);
      }
    },
    [authorizedFetch, loadWalletScreen, session, setAppError],
  );

  const loadMoreLedger = useCallback(async () => {
    if (!session || ledger.length === 0) return;
    const oldest = ledger[ledger.length - 1];
    if (!oldest) return;
    setLedgerLoading(true);
    try {
      const data = await authorizedFetch((token, signal) =>
        fetchLedger(token, { to: oldest.createdAt }, signal),
      );
      const existingIds = new Set(ledger.map((entry) => entry.id));
      const merged = [...ledger, ...data.entries.filter((entry) => !existingIds.has(entry.id))];
      setLedger(merged);
      setLedgerHasMore(data.entries.length >= 100);
    } catch (err) {
      setAppError(err instanceof ApiError ? err.code : "network.offline");
    } finally {
      setLedgerLoading(false);
    }
  }, [authorizedFetch, ledger, session]);

  useEffect(() => {
    if (!session || tab !== "wallet") return;
    void loadWalletScreen();
  }, [session, tab, loadWalletScreen]);

  useEffect(() => {
    if (!session || tab !== "debt") return;
    void loadDebts();
  }, [session, tab, loadDebts]);

  const loadHearthScreen = useCallback(async () => {
    if (!session) return;
    await loadHearth();
    if (summary === null) {
      try {
        const summaryData = await authorizedFetch((token, signal) =>
          fetchEconomySummary(token, signal),
        );
        setSummary(summaryData);
      } catch (err) {
        setAppError(err instanceof ApiError ? err.code : "network.offline");
      }
    }
  }, [authorizedFetch, loadHearth, session, setAppError, summary]);

  useEffect(() => {
    if (!session || tab !== "hearth") return;
    void loadHearthScreen();
  }, [session, tab, loadHearthScreen]);

  const handleFetchFrame = useCallback(
    async (bounds: MapBounds, zoomLevel: number, signal: AbortSignal) => {
      const response = await authorizedFetch(
        (token) => fetchHexes(bounds, zoomLevel, token, signal),
        signal,
      );
      return response.hexes;
    },
    [authorizedFetch],
  );

  const refreshMapAndSelection = useCallback(async () => {
    setMapRefreshKey((key) => key + 1);
    if (!selectedH3 || !session) return;
    try {
      const card = await fetchHexCard(selectedH3, session.accessToken);
      setHexCard(card);
    } catch (err) {
      setAppError(err instanceof ApiError ? err.code : "network.offline");
    }
  }, [selectedH3, session]);

  const handlePerformRitual = useCallback(
    async (traceId: number) => {
      if (!selectedH3 || !session) return;
      setActionBusy(true);
      setErrorCode(null);
      try {
        const card = await authorizedFetch((token) =>
          performRitual(selectedH3, traceId, token, `ritual-web-${selectedH3}-${traceId}`),
        );
        setHexCard(card);
        setCaptureHexId(selectedH3);
        const wallet = await authorizedFetch((token, signal) => fetchWallet(token, signal));
        setWalletServer(wallet);
        await loadTraces();
        await loadFeed(false);
        await loadQuests(false);
        setMapRefreshKey((key) => key + 1);
      } catch (err) {
        setAppError(err instanceof ApiError ? err.code : "network.offline");
      } finally {
        setActionBusy(false);
      }
    },
    [authorizedFetch, loadFeed, loadQuests, loadTraces, selectedH3, session],
  );

  const handleSimulateTrace = useCallback(async () => {
    if (!selectedH3 || !session) return;
    setActionBusy(true);
    setErrorCode(null);
    try {
      const points = buildDebugTracePoints(selectedH3);
      await authorizedFetch((token) =>
        submitTraceBatch(points, token, `trace-debug-${selectedH3}-${Date.now()}`),
      );
      await loadTraces();
      await loadQuests(false);
      await refreshMapAndSelection();
    } catch (err) {
      setAppError(err instanceof ApiError ? err.code : "network.offline");
    } finally {
      setActionBusy(false);
    }
  }, [authorizedFetch, loadQuests, loadTraces, refreshMapAndSelection, selectedH3, session]);

  const handleSetHearth = useCallback(async () => {
    if (!selectedH3 || !session) return;
    setActionBusy(true);
    setErrorCode(null);
    try {
      await placeHearth(selectedH3, session.accessToken, `hearth-web-${selectedH3}`);
      await loadBootstrap();
      await loadHearth();
      const card = await fetchHexCard(selectedH3, session.accessToken);
      setHexCard(card);
    } catch (err) {
      setAppError(err instanceof ApiError ? err.code : "network.offline");
    } finally {
      setActionBusy(false);
    }
  }, [loadBootstrap, loadHearth, selectedH3, session, setAppError]);

  const handleCollectVault = useCallback(async () => {
    if (!selectedH3 || !session) return;
    setActionBusy(true);
    setErrorCode(null);
    try {
      const result = await authorizedFetch((token) =>
        collectVault(selectedH3, token, `collect-${selectedH3}-${Date.now()}`),
      );
      setWalletServer((prev) =>
        prev
          ? {
              ...prev,
              hive: result.wallet.hive,
              debt: result.wallet.debt,
              held: result.wallet.held,
              effective: result.wallet.effective,
            }
          : prev,
      );
      await refreshMapAndSelection();
      await loadQuests(false);
    } catch (err) {
      setAppError(err instanceof ApiError ? err.code : "network.offline");
    } finally {
      setActionBusy(false);
    }
  }, [authorizedFetch, loadQuests, refreshMapAndSelection, selectedH3, session, setAppError]);

  const handleMoveHearth = useCallback(async () => {
    if (!selectedH3 || !session) return;
    setActionBusy(true);
    setErrorCode(null);
    try {
      const hearth = await authorizedFetch((token) =>
        moveHearth(selectedH3, token, `hearth-move-${selectedH3}`),
      );
      setHearthView(hearth);
      setHearthMoveMode(false);
      await loadBootstrap();
      await refreshMapAndSelection();
    } catch (err) {
      setAppError(err instanceof ApiError ? err.code : "network.offline");
    } finally {
      setActionBusy(false);
    }
  }, [authorizedFetch, loadBootstrap, refreshMapAndSelection, selectedH3, session, setAppError]);

  const handleStartHearthMove = useCallback(() => {
    setHearthMoveMode(true);
    setTab("map");
  }, []);

  const handleLogout = useCallback(() => {
    clearSession();
    setSession(null);
    setProfile(null);
    setWalletServer(null);
    setSelectedH3(null);
    setHexCard(null);
    setTab("map");
    setSummary(null);
    setLedger([]);
    setDebts([]);
    setTraces([]);
    setMapRefreshKey(0);
    setFeedEvents([]);
    setFeedUnread(0);
    setFeedOpen(false);
    setDailyQuests([]);
    setQuestOpen(false);
    setHearthView(null);
    setHearthMoveMode(false);
    setTracesOpen(false);
  }, []);

  const handleClaimQuest = useCallback(
    async (slot: number) => {
      if (!session) return;
      setActionBusy(true);
      setErrorCode(null);
      try {
        const result = await authorizedFetch((token) =>
          claimDailyQuest(slot, token, `quest-claim-${slot}-${Date.now()}`),
        );
        setDailyQuests([...result.quests]);
        setQuestEcoDayEndsAt(result.ecoDayEndsAt);
        setWalletServer((prev) =>
          prev ? { ...prev, hive: result.wallet.hive, debt: result.wallet.debt } : prev,
        );
        const me = await authorizedFetch((token, signal) => fetchProfile(token, signal));
        setProfile(me);
      } catch (err) {
        setAppError(err instanceof ApiError ? err.code : "network.offline");
      } finally {
        setActionBusy(false);
      }
    },
    [authorizedFetch, session],
  );

  const handleMarkFeedRead = useCallback(async () => {
    if (!session) return;
    setActionBusy(true);
    try {
      await authorizedFetch((token) => markFeedRead(token, { all: true }));
      await loadFeed(false);
      setFeedOpen(false);
    } catch (err) {
      setAppError(err instanceof ApiError ? err.code : "network.offline");
    } finally {
      setActionBusy(false);
    }
  }, [authorizedFetch, loadFeed, session]);

  const handleOpenFeedHex = useCallback((hexId: string) => {
    setFeedOpen(false);
    setTab("map");
    setSelectedH3(hexId);
  }, []);

  const handleOpenTracesHex = useCallback((hexId: string) => {
    setTracesOpen(false);
    setTab("map");
    setSelectedH3(hexId);
  }, []);

  const handleOpenDebts = useCallback(() => {
    setTab("debt");
  }, []);

  const handleOpenReport = useCallback((context?: SupportReportContext) => {
    setReportContext(context);
    setReportNoticeKey(null);
    setReportNoticeParams({});
    setReportOpen(true);
    setSettingsOpen(false);
  }, []);

  const handleSubmitReport = useCallback(
    async (category: SupportCategory, message: string) => {
      if (!session) return;
      setActionBusy(true);
      setReportNoticeKey(null);
      try {
        const geoStats = category === "geolocation" ? getTraceTelemetry() ?? undefined : undefined;
        const result = await authorizedFetch((token) =>
          submitSupportReport(
            {
              category,
              message,
              clientVersion: "0.1.0",
              clientPlatform: "web",
              locale,
              ...(reportContext !== undefined ? { context: reportContext } : {}),
              clientErrors: [...snapshotClientErrors()],
              ...(geoStats !== undefined ? { geoStats } : {}),
            },
            token,
            `support-${Date.now()}`,
          ),
        );
        clearClientErrors();
        setReportNoticeKey("support:report.success");
        setReportNoticeParams({ reportId: result.reportId });
      } catch (err) {
        setAppError(err instanceof ApiError ? err.code : "network.offline");
      } finally {
        setActionBusy(false);
      }
    },
    [authorizedFetch, locale, reportContext, session, setAppError],
  );

  if (!session) {
    return (
      <div className="app app--auth">
        <AuthPanel defaultEmail={uiPrefs.lastEmail} onSession={setSession} />
      </div>
    );
  }

  const distance =
    selectedH3 && hearthH3
      ? distanceMeters(hexCenter(hearthH3), hexCenter(selectedH3))
      : null;

  const showDebtTab = (walletServer?.debt ?? 0) > 0 || debts.length > 0;

  // Подсказка первых шагов (§24.3). Считается по состоянию игрока, поэтому
  // сама гаснет, как только шаг сделан — хоть на сайте, хоть на телефоне.
  const onboardingStep = profile
    ? nextOnboardingStep({
        hasHearth: hearthH3 !== null,
        hasValidTrace: traces.some((trace) =>
          isRitualTraceQuality(trace.quality as TraceQuality),
        ),
        ownsHex: (profile.ownedHexes ?? 0) > 0,
        // Точный остаток казны лежит в карточке соты, а подсказке хватает признака
        // «соты сегодня что-то накопили»: он приходит в суточной сводке.
        vaultReady: (summary?.incomeHex ?? 0) > 0,
      })
    : null;
  const showHint = onboardingStep !== null && onboardingStep !== hintDismissed;
  const shieldActive =
    profile !== null &&
    profile.shieldUntil !== null &&
    new Date(profile.shieldUntil).getTime() > Date.now();

  function handleAppearanceChange(next: HeroAppearanceId): void {
    setAppearance(next);
    saveUiPrefs({ heroAppearance: next });
  }

  return (
    <div className={`app${tab !== "map" ? " app--screen" : ""}`}>
      <div className="overlay overlay--stack-top">
        {/*
          Шапка профиля стоит на всех экранах, кроме самого экрана героя: там
          ник, уровень и фигура уже показаны крупно. Деньги в шапке появляются
          только там, где их больше нигде нет: на карте своя панель кошелька, а
          экраны кошелька и долга сами про деньги и подробнее шапки.
        */}
        {profile && tab !== "hero" && (
          <ProfileBar
            locale={locale}
            appearanceId={appearance}
            nickname={profile.nickname}
            level={profile.level}
            atCap={profile.level >= HERO_LEVEL_CAP}
            shielded={shieldActive}
            wallet={tab === "map" || tab === "wallet" || tab === "debt" ? null : wallet}
            onOpen={() => setTab("hero")}
          />
        )}

        {(loading || errorCode || verifyNotice || gpsHint) && (
          <div className="stack-top__status" role="status">
            {loading && <p>{t("common:state.loading")}</p>}
            {gpsHint && !errorCode && (
              <p className="app__hint">{t(errorI18nKey(gpsHint), { defaultValue: gpsHint })}</p>
            )}
            {verifyNotice && (
              <p className="app__notice" role="status">
                {t(verifyNotice, { defaultValue: verifyNotice })}
              </p>
            )}
            {errorCode && (
              <div className="app__error" role="alert">
                <span className="app__error-text">
                  {t(errorI18nKey(errorCode), { defaultValue: errorCode })}
                </span>
                <span className="app__error-actions">
                  <button type="button" className="button" onClick={() => void loadBootstrap()}>
                    {t("common:action.retry")}
                  </button>
                  {(errorCode === "auth.required" || errorCode === "auth.refresh_invalid") && (
                    <button type="button" className="button" onClick={handleLogout}>
                      {t("common:auth.login")}
                    </button>
                  )}
                </span>
              </div>
            )}
          </div>
        )}

        {profile && profile.emailVerified === false && session && !verifyHidden && (
          <VerifyBanner
            state={
              verifyNotice === "common:auth.verify_success"
                ? "ok"
                : verifyNotice?.startsWith("errors:")
                  ? "error"
                  : "pending"
            }
            messageKey={
              verifyNotice === "common:auth.verify_success" ||
              verifyNotice?.startsWith("errors:")
                ? verifyNotice
                : undefined
            }
            onResend={() => void handleResendVerification()}
            onDismiss={handleDismissVerify}
          />
        )}

        {tab === "map" && showHint && onboardingStep && (
          <HintBar step={onboardingStep} onDismiss={() => setHintDismissed(onboardingStep)} />
        )}

        {tab === "map" && wallet && (
          <>
            <WalletBar locale={locale} wallet={wallet} />
            <div className="top-actions">
              <button
                type="button"
                className="top-actions__btn button"
                onClick={() => setQuestOpen(true)}
              >
                {t("quests:action.open")}
              </button>
              <button
                type="button"
                className="top-actions__btn button"
                onClick={() => setFeedOpen(true)}
              >
                {feedUnread > 0
                  ? t("feed:banner.unread", { count: feedUnread })
                  : t("feed:action.open")}
              </button>
              <button
                type="button"
                className="top-actions__btn button"
                onClick={() => setTracesOpen(true)}
              >
                {traces.length > 0
                  ? t("traces:action.open_count", { count: traces.length })
                  : t("traces:action.open")}
              </button>
            </div>
          </>
        )}
      </div>

      {tab === "map" && (
        <>
          <HiveMap
            center={DEFAULT_CENTER}
            hearthH3={hearthH3}
            selectedH3={selectedH3}
            refreshKey={mapRefreshKey}
            captureHexId={captureHexId}
            onCaptureAnimationEnd={() => setCaptureHexId(null)}
            onSelect={setSelectedH3}
            onZoomChange={setZoom}
            onFetchFrame={handleFetchFrame}
            onFrameError={setAppError}
          />

          {hearthMoveMode && (
            <div className="overlay overlay--move-hint" role="status">
              <p>{t("hearth:move.mode")}</p>
              <button type="button" className="button" onClick={() => setHearthMoveMode(false)}>
                {t("common:action.cancel")}
              </button>
            </div>
          )}

          <div className="overlay overlay--left">
            <Legend locale={locale} hexesVisible={zoom >= MIN_HEX_ZOOM} />
          </div>

          {hexCard && selectedH3 && !hexLoading && (
            <div className="overlay overlay--right">
              <HexCard
                locale={locale}
                card={hexCard}
                distanceFromHearth={distance}
                isHearth={selectedH3 === hearthH3}
                isHearthRing={hearthRing.has(selectedH3) && selectedH3 !== hearthH3}
                hasHearth={hearthH3 !== null}
                traces={traces}
                onClose={() => setSelectedH3(null)}
                onSetHearth={() => void handleSetHearth()}
                onPerformRitual={(traceId) => void handlePerformRitual(traceId)}
                onSimulateTrace={() => void handleSimulateTrace()}
                onCollectVault={() => void handleCollectVault()}
                onMoveHearth={() => void handleMoveHearth()}
                hearthMoveMode={hearthMoveMode}
                busy={actionBusy}
              />
            </div>
          )}
        </>
      )}

      {questOpen && (
        <div className="overlay overlay--feed">
          <QuestScreen
            locale={locale}
            ecoDayEndsAt={questEcoDayEndsAt}
            quests={dailyQuests}
            loading={questLoading}
            busy={actionBusy}
            onClose={() => setQuestOpen(false)}
            onClaim={(slot) => void handleClaimQuest(slot)}
          />
        </div>
      )}

      {feedOpen && (
        <div className="overlay overlay--feed">
          <FeedScreen
            locale={locale}
            timeZone={timeZone}
            events={feedEvents}
            unreadCount={feedUnread}
            loading={feedLoading}
            onClose={() => setFeedOpen(false)}
            onMarkRead={() => void handleMarkFeedRead()}
            onOpenHex={handleOpenFeedHex}
          />
        </div>
      )}

      {tab === "hero" && profile && (
        <HeroScreen
          locale={locale}
          timeZone={timeZone}
          profile={profile}
          appearance={appearance}
          onAppearanceChange={handleAppearanceChange}
        />
      )}

      {tab === "hearth" && (
        <HearthScreen
          locale={locale}
          timeZone={timeZone}
          hearth={hearthView}
          hearthLoading={hearthLoading}
          summary={summary}
          onOpenMap={() => setTab("map")}
          onStartMove={handleStartHearthMove}
        />
      )}

      {tracesOpen && (
        <div className="overlay overlay--feed">
          <TracesScreen
            locale={locale}
            traces={traces}
            onClose={() => setTracesOpen(false)}
            onOpenHex={handleOpenTracesHex}
          />
        </div>
      )}

      {tab === "wallet" && wallet && walletServer && (
        <WalletScreen
          locale={locale}
          timeZone={timeZone}
          wallet={wallet}
          walletServer={walletServer}
          summary={summary}
          ledger={ledger}
          ledgerLoading={ledgerLoading}
          onLoadMoreLedger={() => void loadMoreLedger()}
          ledgerHasMore={ledgerHasMore}
          onOpenSettings={() => setSettingsOpen(true)}
          onReportLedgerEntry={(entryId) => handleOpenReport({ ledgerEntryId: entryId })}
          convertBusy={actionBusy}
          onConvert={handleConvertCash}
          {...(showDebtTab ? { onOpenDebts: handleOpenDebts } : {})}
        />
      )}

      {introOpen && (
        <div className="overlay overlay--feed">
          <OnboardingSheet onClose={handleCloseIntro} />
        </div>
      )}

      {settingsOpen && (
        <div className="overlay overlay--feed">
          <SettingsScreen
            onClose={() => setSettingsOpen(false)}
            onReport={() => handleOpenReport()}
            showVerifyEmail={profile?.emailVerified === false && verifyHidden}
            onVerifyEmail={handleShowVerify}
            onShowHints={handleShowIntro}
          />
        </div>
      )}

      {reportOpen && (
        <div className="overlay overlay--feed">
          <ReportProblemSheet
            {...(reportContext !== undefined ? { context: reportContext } : {})}
            busy={actionBusy}
            noticeKey={reportNoticeKey}
            noticeParams={reportNoticeParams}
            onClose={() => setReportOpen(false)}
            onSubmit={(category, message) => void handleSubmitReport(category, message)}
          />
        </div>
      )}

      {tab === "debt" && walletServer && profile && (
        <DebtScreen
          locale={locale}
          timeZone={timeZone}
          level={profile.level}
          wallet={walletServer}
          debts={debts}
          loading={debtsLoading}
        />
      )}

      <div className="overlay overlay--bottom">
        <AppNav active={tab} onChange={setTab} showDebt={showDebtTab} />
      </div>
    </div>
  );
}

export { hexAt } from "./map/hexes.js";
